import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle,
  Minus,
  Plus,
  RotateCcw,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import ProjectIntroLanding from "@/components/public/ProjectIntroLanding";

function pointInPolygon(point, polygon) {
  let inside = false;

  for (
    let index = 0, previous = polygon.length - 1;
    index < polygon.length;
    previous = index++
  ) {
    const [x, y] = polygon[index];
    const [previousX, previousY] = polygon[previous];

    const intersects =
      (y > point[1]) !== (previousY > point[1]) &&
      point[0] <
        ((previousX - x) * (point[1] - y)) /
          (previousY - y) +
          x;

    if (intersects) inside = !inside;
  }

  return inside;
}

/* ============================================================
   CAPAS DEL PLAN
   ============================================================ */

function PublicOverlay({ availableParcels, layer }) {
  const labelClass =
    layer.id === "NUM_LOTE"
      ? " public-overlay-lot-numbers"
      : layer.id === "NUM_UV_MZA"
        ? " public-overlay-manzano"
        : "";

  /*
   * Las capas pueden traer color/opacidad.
   * Si no tienen color, NO debemos permitir que SVG
   * utilice el negro por defecto.
   */
  const layerColor =
    layer.color ||
    layer.fill ||
    layer.stroke ||
    null;

  const layerOpacity =
    layer.opacity !== undefined
      ? layer.opacity
      : 1;

  return (
    <g
  className={`public-overlay public-overlay-${layer.category}${labelClass}`}
  key={layer.id}
  style={{
    pointerEvents: "none",
  }}
>
      {Array.isArray(layer.shapes) &&
        layer.shapes.map((shape, index) => {
          const key = `${layer.id}-${shape.type}-${index}`;

          /*
           * ==================================================
           * LINE
           * ==================================================
           */
          if (shape.type === "line") {
            if (
              !Array.isArray(shape.points) ||
              shape.points.length < 2
            ) {
              return null;
            }

            /*
             * Una línea siempre debe ser transparente por dentro.
             */
            const points = shape.points
              .map((point) => point.join(","))
              .join(" ");

            return (
              <polyline
                key={key}
                points={points}
                fill="none"
                stroke={layerColor || undefined}
                strokeOpacity={
                  layerColor ? layerOpacity : undefined
                }
                vectorEffect="non-scaling-stroke"
              />
            );
          }

          /*
           * ==================================================
           * POLYLINE
           * ==================================================
           */
          if (shape.type === "polyline") {
            if (
              !Array.isArray(shape.points) ||
              shape.points.length < 2
            ) {
              return null;
            }

            const points = shape.points
              .map((point) => point.join(","))
              .join(" ");

            return (
              <polyline
                key={key}
                points={points}
                fill="none"
                stroke={layerColor || undefined}
                strokeOpacity={
                  layerColor ? layerOpacity : undefined
                }
                vectorEffect="non-scaling-stroke"
              />
            );
          }

          /*
           * ==================================================
           * POLYGON
           * ==================================================
           *
           * IMPORTANTE:
           * Si la capa no tiene color definido, NO pintamos
           * el polígono. Esto evita el fondo negro de SVG.
           */
          /*
 * ==================================================
 * POLYGON
 * ==================================================
 */
if (shape.type === "polygon") {
  if (
    !Array.isArray(shape.points) ||
    shape.points.length < 3
  ) {
    return null;
  }

  const points = shape.points
    .map((point) => point.join(","))
    .join(" ");

  /*
   * ============================================================
   * AGUA
   *
   * IMPORTANTE:
   * Esta comprobación DEBE estar ANTES de comprobar
   * layerColor porque las capas de agua pueden no traer
   * color en plan-overlays.json.
   * ============================================================
   */
  if (layer.category === "water") {
    return (
      <g
        key={`${layer.id}-water-${index}`}
        className="public-water-shape"
        pointerEvents="none"
      >
        {/* Base del agua */}
        <polygon
          points={points}
          className="public-water-base"
          fill="url(#publicWaterGradient)"
          fillOpacity={0.92}
          stroke="#3e8eac"
          strokeOpacity={0.9}
          strokeWidth="1.4"
          vectorEffect="non-scaling-stroke"
        />

        {/* Ondas / reflejos */}
        <polygon
          points={points}
          className="public-water-waves"
          fill="url(#publicWaterPattern)"
          fillOpacity={0.78}
          stroke="none"
          vectorEffect="non-scaling-stroke"
        />

        {/* Borde */}
        <polygon
          points={points}
          className="public-water-border"
          fill="none"
          stroke="#3e8eac"
          strokeWidth="1.4"
          strokeOpacity={0.9}
          vectorEffect="non-scaling-stroke"
        />
      </g>
    );
  }

  /*
   * ============================================================
   * CAPAS NORMALES
   * ============================================================
   */

  if (!layerColor) {
    return (
      <polygon
        key={key}
        points={points}
        fill="none"
        stroke="none"
      />
    );
  }

  return (
    <polygon
      key={`${layer.id}-shape-${index}`}
      points={points}
      fill={layerColor}
      fillOpacity={layerOpacity}
      stroke={layerColor}
      strokeOpacity={layerOpacity}
      vectorEffect="non-scaling-stroke"
    />
  );
}
          /*
           * ==================================================
           * CIRCLE
           * ==================================================
           */
            if (shape.type === "circle") {
            if (!Array.isArray(shape.center)) {
              return null;
            }

            return (
              <circle
                key={key}
                cx={shape.center[0]}
                cy={shape.center[1]}
                r={shape.radius || 1}
                fill={layerColor || "none"}
                fillOpacity={
                  layerColor ? layerOpacity : 0
                }
                stroke={layerColor || "none"}
                strokeOpacity={
                  layerColor ? layerOpacity : 0
                }
                vectorEffect="non-scaling-stroke"
              />
            );
          }

          /*
           * ==================================================
           * TEXT
           * ==================================================
           */
          if (shape.type === "text") {
            if (!Array.isArray(shape.point)) {
              return null;
            }

            const isLotNumber =
              layer.id === "NUM_LOTE";

            /*
             * Los números de lote solamente aparecen
             * sobre lotes disponibles.
             */
            if (
              isLotNumber &&
              !availableParcels.some((parcel) =>
                pointInPolygon(
                  shape.point,
                  parcel.points
                )
              )
            ) {
              return null;
            }

            const fontSize = isLotNumber
              ? (shape.height || 10) * 0.46
              : shape.height || 10;

            return (
              <text
                key={key}
                x={shape.point[0]}
                y={shape.point[1]}
                fontSize={fontSize}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={layerColor || undefined}
                fillOpacity={
                  layerColor ? layerOpacity : undefined
                }
              >
                {shape.value}
              </text>
            );
          }

          return null;
        })}
    </g>
  );
}

export default function PublicPlanPage() {
  const [plan, setPlan] = useState(null);
  const [overlays, setOverlays] = useState([]);
  const [lots, setLots] = useState([]);
  const [visibility, setVisibility] = useState({});

  const [selected, setSelected] = useState(null);
  const [viewBox, setViewBox] = useState(null);
  const [drag, setDrag] = useState(null);

  const [visitId, setVisitId] = useState(() => {
  return sessionStorage.getItem("psc-public-visit-id") || null;
});

  // Después de registrar los datos mostramos primero
  // la presentación del proyecto antes del plano.
  const [showProjectLanding, setShowProjectLanding] = useState(false);

  const [visitorForm, setVisitorForm] = useState({
    nombre: "",
    telefono: "",
  });

  const [visitorLoading, setVisitorLoading] = useState(false);
  const [visitorError, setVisitorError] = useState("");

  const svgRef = useRef(null);
  const suppressClickRef = useRef(false);

  const registeredInterestsRef = useRef(new Set());

  /* ============================================================
     CARGA
     ============================================================ */

  useEffect(() => {
    let cancelled = false;

    async function loadPlan() {
      try {
        const [
          planResponse,
          overlaysResponse,
          lotsResponse,
        ] = await Promise.all([
          fetch("/plan-preview.json"),
          fetch("/plan-overlays.json"),
          api.get("/public-plan"),
        ]);

        if (!planResponse.ok) {
          throw new Error(
            `plan-preview.json respondió ${planResponse.status}`
          );
        }

        if (!overlaysResponse.ok) {
          throw new Error(
            `plan-overlays.json respondió ${overlaysResponse.status}`
          );
        }

        const planData = await planResponse.json();
        const overlaysData = await overlaysResponse.json();

        if (cancelled) return;

        setPlan(planData);
        setOverlays(overlaysData.layers || []);
        setLots(lotsResponse.data.lots || []);
        setVisibility(lotsResponse.data.visibility || {});

        setViewBox(
          `0 0 ${planData.bounds.width} ${planData.bounds.height}`
        );
      } catch (error) {
        console.error("Error cargando plano público:", error);
      }
    }

    loadPlan();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ============================================================
     LOTES
     ============================================================ */

  const lotsByPlan = useMemo(
    () =>
      new Map(
        lots.map((lot) => [lot.plan_id, lot])
      ),
    [lots]
  );

  const selectedLot = selected
    ? lotsByPlan.get(selected)
    : null;

  const availableParcels = useMemo(
    () =>
      lots
        .map((lot) =>
          plan?.lots?.find(
            (parcel) => parcel.id === lot.plan_id
          )
        )
        .filter(Boolean),
    [lots, plan]
  );

  /* ============================================================
     VISITA
     ============================================================ */

  const handleVisitorSubmit = async (event) => {
    event.preventDefault();

    const nombre = visitorForm.nombre.trim();
    const telefono = visitorForm.telefono.trim();

    if (!nombre || !telefono) {
      setVisitorError(
        "Por favor completa tu nombre y teléfono."
      );
      return;
    }

    setVisitorError("");
    setVisitorLoading(true);

    try {
      const response = await api.post(
        "/public-plan/visits",
        {
          nombre,
          telefono,
        }
      );

      const newVisitId = response.data.visit_id;

      if (!newVisitId) {
        throw new Error(
          "No se recibió el identificador de la visita."
        );
      }

      setVisitId(newVisitId);
      sessionStorage.setItem("psc-public-visit-id", newVisitId);
      setShowProjectLanding(true);
    } catch (error) {
      console.error(
        "Error registrando visita:",
        error
      );

      setVisitorError(
        error?.response?.data?.detail ||
          "No fue posible continuar. Intenta nuevamente."
      );
    } finally {
      setVisitorLoading(false);
    }
  };

  /* ============================================================
     INTERÉS EN LOTE
     ============================================================ */

  const registerLotInterest = async (lot) => {
  const activeVisitId =
    visitId ||
    sessionStorage.getItem("psc-public-visit-id");

  console.log("===== REGISTRAR INTERÉS =====");
  console.log("activeVisitId:", activeVisitId);
  console.log("visitId state:", visitId);
  console.log("lot:", lot);

  if (!activeVisitId || !lot?.id) {
    console.warn("NO SE REGISTRA:", {
      activeVisitId,
      lotId: lot?.id,
    });
    return;
  }

  if (registeredInterestsRef.current.has(lot.id)) {
    console.log("LOTE YA REGISTRADO:", lot.id);
    return;
  }

  registeredInterestsRef.current.add(lot.id);

  try {
    console.log("ENVIANDO POST:", {
      url: `/public-plan/visits/${activeVisitId}/interest`,
      lot_id: lot.id,
    });

    const response = await api.post(
      `/public-plan/visits/${activeVisitId}/interest`,
      {
        lot_id: lot.id,
      }
    );

    console.log("===== INTERÉS REGISTRADO =====");
    console.log(response.data);
  } catch (error) {
    console.error(
      "===== ERROR REGISTRANDO INTERÉS =====",
      error?.response?.data || error
    );

    registeredInterestsRef.current.delete(lot.id);
  }
};

  /* ============================================================
     WHATSAPP
     ============================================================ */

  const whatsappUrl = selectedLot
    ? (() => {
        const lines = [
          "Hola, estoy interesado/a en este terreno disponible.",
          `Manzano: ${selectedLot.manzano}`,
          `Lote: ${selectedLot.lote}`,

          visibility.show_surface &&
            `Superficie: ${selectedLot.superficie} m²`,

          visibility.show_price_sqm &&
            `Precio por m²: US$ ${selectedLot.precio_m2}`,

          visibility.show_total_price &&
            `Precio total: US$ ${selectedLot.precio_total}`,

          visibility.show_phase &&
            `Fase: ${selectedLot.fase || "—"}`,

          `Plano: ${window.location.origin}/visor-plano`,
        ]
          .filter(Boolean)
          .join("\n");

        return `https://wa.me/${
          visibility.whatsapp_number ||
          "59177987826"
        }?text=${encodeURIComponent(lines)}`;
      })()
    : "";

  /* ============================================================
     ZOOM
     ============================================================ */

  const zoom = (factor) => {
    if (!viewBox || !plan) return;

    const [
      x,
      y,
      width,
      height,
    ] = viewBox.split(" ").map(Number);

    const nextWidth = Math.min(
      plan.bounds.width,
      Math.max(
        plan.bounds.width * 0.08,
        width * factor
      )
    );

    const nextHeight = Math.min(
      plan.bounds.height,
      Math.max(
        plan.bounds.height * 0.08,
        height * factor
      )
    );

    setViewBox(
      `${x + (width - nextWidth) / 2} ${
        y + (height - nextHeight) / 2
      } ${nextWidth} ${nextHeight}`
    );
  };

  const resetView = () => {
    if (!plan) return;

    setViewBox(
      `0 0 ${plan.bounds.width} ${plan.bounds.height}`
    );
  };

  const wheelZoom = (event) => {
    event.preventDefault();

    zoom(
      event.deltaY > 0
        ? 1.18
        : 0.84
    );
  };

    /* ============================================================
     ARRASTRE + ZOOM TÁCTIL
     ============================================================ */

  const touchPointersRef = useRef(new Map());

  const pinchRef = useRef(null);

  const beginDrag = (event) => {
    if (!viewBox) return;

    /*
     * ============================================================
     * MODO TÁCTIL
     * ============================================================
     */

    if (event.pointerType === "touch") {
      touchPointersRef.current.set(
        event.pointerId,
        {
          x: event.clientX,
          y: event.clientY,
        }
      );

      /*
       * Dos dedos = PINCH ZOOM
       */
      if (touchPointersRef.current.size >= 2) {
        suppressClickRef.current = true;

        setDrag(null);

        const pointers = Array.from(
          touchPointersRef.current.values()
        );

        const first = pointers[0];
        const second = pointers[1];

        const midpoint = {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2,
        };

        const distance = Math.hypot(
          second.x - first.x,
          second.y - first.y
        );

        pinchRef.current = {
          viewBox,
          midpoint,
          distance,
        };

        return;
      }

      /*
       * Un dedo = desplazamiento
       */
      if (touchPointersRef.current.size === 1) {
        suppressClickRef.current = false;

        setDrag({
          x: event.clientX,
          y: event.clientY,
          viewBox,
        });

        try {
          svgRef.current?.setPointerCapture?.(
            event.pointerId
          );
        } catch (_) {}

        return;
      }
    }

    /*
     * ============================================================
     * MOUSE / POINTER NORMAL
     * ============================================================
     */

    suppressClickRef.current = false;

    setDrag({
      x: event.clientX,
      y: event.clientY,
      viewBox,
    });

    try {
      svgRef.current?.setPointerCapture?.(
        event.pointerId
      );
    } catch (_) {}
  };

  const moveDrag = (event) => {
    if (!svgRef.current) {
      return;
    }

    /*
     * ============================================================
     * ZOOM MULTITÁCTIL
     * ============================================================
     */

    if (event.pointerType === "touch") {
      const pointer = touchPointersRef.current.get(
        event.pointerId
      );

      if (pointer) {
        pointer.x = event.clientX;
        pointer.y = event.clientY;
      }

      /*
       * Tenemos dos dedos activos.
       */
      if (
        touchPointersRef.current.size >= 2 &&
        pinchRef.current
      ) {
        event.preventDefault();

        const pointers = Array.from(
          touchPointersRef.current.values()
        );

        const first = pointers[0];
        const second = pointers[1];

        const currentMidpoint = {
          x: (first.x + second.x) / 2,
          y: (first.y + second.y) / 2,
        };

        const currentDistance = Math.hypot(
          second.x - first.x,
          second.y - first.y
        );

        if (
          !currentDistance ||
          !pinchRef.current.distance
        ) {
          return;
        }

        const [
          startX,
          startY,
          startWidth,
          startHeight,
        ] = pinchRef.current.viewBox
          .split(" ")
          .map(Number);

        const rect =
          svgRef.current.getBoundingClientRect();

        if (!rect.width || !rect.height) {
          return;
        }

        /*
         * Si los dedos se separan:
         * currentDistance > initialDistance
         * => hacemos zoom IN.
         *
         * Si se acercan:
         * => hacemos zoom OUT.
         */
        const scale =
          pinchRef.current.distance /
          currentDistance;

        const nextWidth = Math.min(
          plan.bounds.width,
          Math.max(
            plan.bounds.width * 0.08,
            startWidth * scale
          )
        );

        const nextHeight = Math.min(
          plan.bounds.height,
          Math.max(
            plan.bounds.height * 0.08,
            startHeight * scale
          )
        );

        /*
         * Punto del plano que estaba debajo del
         * centro del gesto.
         */
        const anchorX =
          startX +
          ((pinchRef.current.midpoint.x -
            rect.left) /
            rect.width) *
            startWidth;

        const anchorY =
          startY +
          ((pinchRef.current.midpoint.y -
            rect.top) /
            rect.height) *
            startHeight;

        /*
         * Mantenemos ese punto debajo del centro
         * actual de los dedos.
         */
        const nextX =
          anchorX -
          ((currentMidpoint.x - rect.left) /
            rect.width) *
            nextWidth;

        const nextY =
          anchorY -
          ((currentMidpoint.y - rect.top) /
            rect.height) *
            nextHeight;

        setViewBox(
          `${nextX} ${nextY} ${nextWidth} ${nextHeight}`
        );

        suppressClickRef.current = true;

        return;
      }
    }

    /*
     * ============================================================
     * ARRASTRE NORMAL
     * ============================================================
     */

    if (!drag) {
      return;
    }

    const [
      x,
      y,
      width,
      height,
    ] = drag.viewBox.split(" ").map(Number);

    const rect =
      svgRef.current.getBoundingClientRect();

    if (!rect.width || !rect.height) {
      return;
    }

    const deltaX =
      ((event.clientX - drag.x) /
        rect.width) *
      width;

    const deltaY =
      ((event.clientY - drag.y) /
        rect.height) *
      height;

    if (
      Math.abs(event.clientX - drag.x) > 4 ||
      Math.abs(event.clientY - drag.y) > 4
    ) {
      suppressClickRef.current = true;
    }

    setViewBox(
      `${x - deltaX} ${
        y - deltaY
      } ${width} ${height}`
    );
  };

  const endDrag = (event) => {
    /*
     * ============================================================
     * FINALIZAR TOUCH
     * ============================================================
     */

    if (event.pointerType === "touch") {
      touchPointersRef.current.delete(
        event.pointerId
      );

      /*
       * Si quedan dos dedos, reconstruimos el pinch
       * para que el gesto pueda continuar.
       */
      if (touchPointersRef.current.size >= 2) {
        const pointers = Array.from(
          touchPointersRef.current.values()
        );

        const first = pointers[0];
        const second = pointers[1];

        pinchRef.current = {
          viewBox,
          midpoint: {
            x:
              (first.x + second.x) /
              2,
            y:
              (first.y + second.y) /
              2,
          },
          distance: Math.hypot(
            second.x - first.x,
            second.y - first.y
          ),
        };

        return;
      }

      /*
       * Al quedar un solo dedo dejamos que continúe
       * como arrastre.
       */
      if (touchPointersRef.current.size === 1) {
        const remaining =
          Array.from(
            touchPointersRef.current.entries()
          )[0];

        const pointerId = remaining[0];
        const pointer = remaining[1];

        pinchRef.current = null;

        setDrag({
          x: pointer.x,
          y: pointer.y,
          viewBox,
        });

        try {
          svgRef.current?.setPointerCapture?.(
            pointerId
          );
        } catch (_) {}

        return;
      }

      /*
       * Ya no quedan dedos.
       */
      pinchRef.current = null;
    }

    try {
      svgRef.current?.releasePointerCapture?.(
        event.pointerId
      );
    } catch (_) {}

    setDrag(null);
  };
  /* ============================================================
     CLICK LOTE
     ============================================================ */

  const handleLotClick = (event, parcel) => {
  event.preventDefault();
  event.stopPropagation();

  if (suppressClickRef.current) {
    suppressClickRef.current = false;
    return;
  }

  const lot = lotsByPlan.get(parcel.id);

  console.log("===== LOTE CLICK =====");
  console.log("parcel.id:", parcel.id);
  console.log("lot:", lot);
  console.log("visitId:", visitId);
  console.log(
    "session visit:",
    sessionStorage.getItem("psc-public-visit-id")
  );

  if (!lot) {
    console.warn(
      "No existe lote disponible para parcel.id:",
      parcel.id
    );
    return;
  }

  setSelected(parcel.id);

  const activeVisitId =
    visitId ||
    sessionStorage.getItem("psc-public-visit-id");

  if (activeVisitId) {
    registerLotInterest(lot);
  } else {
    console.warn(
      "LOTE NO REGISTRADO: no existe visita activa"
    );
  }
};

  /* ============================================================
     CARGANDO
     ============================================================ */

  if (!plan) {
    return (
      <div className="public-plan-loading">
        Cargando disponibilidad...
      </div>
    );
  }

  /*
   * IMPORTANTE:
   * Las capas de dimensiones no se muestran.
   * Las demás capas se toman DIRECTAMENTE de
   * plan-overlays.json.
   */
  const visibleOverlays = overlays.filter(
    (layer) =>
      layer.enabled !== false &&
      layer.category !== "dimension"
  );

  /* ============================================================
     FORMULARIO
     ============================================================ */

  if (!visitId) {
  return (
    <div className="public-plan-entry">
      <div className="public-plan-entry-card">

        <div className="public-plan-entry-icon">
          <MessageCircle size={26} />
        </div>

        <div className="public-plan-entry-header">
          <span className="public-plan-entry-eyebrow">
            PUERTO SANTA CRUZ
          </span>

          <h1>
            Encuentra tu
            <span> próximo terreno</span>
          </h1>

          <p>
            Explora nuestro plano interactivo y
            descubre los terrenos actualmente
            disponibles.
          </p>
        </div>

        <div className="public-plan-entry-highlight">
          <div className="public-plan-entry-highlight-dot" />

          <div>
            <strong>
              Consulta disponibilidad
            </strong>

            <span>
              Selecciona un lote y conoce sus
              características y precio.
            </span>
          </div>
        </div>

        <form
          className="public-plan-entry-form"
          onSubmit={handleVisitorSubmit}
        >

          <label>
            <span>¿Cómo te llamas?</span>

            <input
              type="text"
              value={visitorForm.nombre}
              onChange={(event) =>
                setVisitorForm((current) => ({
                  ...current,
                  nombre: event.target.value,
                }))
              }
              placeholder="Escribe tu nombre"
              autoComplete="name"
              required
            />
          </label>

          <label>
            <span>Tu número de teléfono</span>

            <input
              type="tel"
              value={visitorForm.telefono}
              onChange={(event) =>
                setVisitorForm((current) => ({
                  ...current,
                  telefono: event.target.value,
                }))
              }
              placeholder="Ej. 70000000"
              autoComplete="tel"
              required
            />
          </label>

          {visitorError && (
            <div className="public-plan-entry-error">
              {visitorError}
            </div>
          )}

          <button
            type="submit"
            disabled={visitorLoading}
            className="public-plan-entry-button"
          >
            <span>
              {visitorLoading
                ? "Preparando el plano..."
                : "Explorar terrenos"}
            </span>

            {!visitorLoading && (
              <span className="public-plan-entry-arrow">
                →
              </span>
            )}
          </button>
        </form>

        <div className="public-plan-entry-footer">
          <span>
            Tu información nos permitirá
            brindarte una atención personalizada.
          </span>
        </div>

      </div>
    </div>
  );
}

  /* ============================================================
     LANDING DEL PROYECTO
     ============================================================ */

  if (visitId && showProjectLanding) {
    return (
      <ProjectIntroLanding
        onViewPlan={() => {
          setShowProjectLanding(false);
        }}
      />
    );
  }

  /* ============================================================
     VISOR
     ============================================================ */

  
    return (
  <div className="public-plan-page">

    {/* =====================================================
        VOLVER A LA LANDING
        La visita ya está registrada, por lo que NO
        vuelve a mostrar el formulario.
        ===================================================== */}

    <div className="public-plan-brand-controls">

  <div className="public-plan-brand">
    <img
      src="/puerto-santa-cruz-logo.png"
      alt="Puerto Santa Cruz Yacht Club Resort"
    />

    <div>
      <strong>PUERTO SANTA CRUZ</strong>
      <span>YACHT CLUB RESORT</span>
    </div>
  </div>

  <button
    type="button"
    className="public-plan-back-button"
    onClick={() => {
      setSelected(null);
      setShowProjectLanding(true);
    }}
    aria-label="Volver al proyecto"
  >
    ← Volver al proyecto
  </button>

</div>

    <div className="public-plan-toolbar">
        <button
          aria-label="Alejar plano"
          className="icon-button"
          data-testid="public-plan-zoom-out"
          onClick={() => zoom(1.4)}
          type="button"
        >
          <Minus size={18} />
        </button>

        <button
          aria-label="Acercar plano"
          className="icon-button"
          data-testid="public-plan-zoom-in"
          onClick={() => zoom(0.7)}
          type="button"
        >
          <Plus size={18} />
        </button>

        <button
          aria-label="Restablecer plano"
          className="icon-button"
          onClick={resetView}
          type="button"
        >
          <RotateCcw size={18} />
        </button>
      </div>

      <svg
        className="public-plan-svg"
        data-testid="public-plan-svg"
        ref={svgRef}
        viewBox={viewBox}
        style={{ touchAction: "none" }}
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={wheelZoom}
      >
        <defs>

  {/* =========================================================
      DEGRADADO DEL AGUA
      ========================================================= */}

  <linearGradient
    id="publicWaterGradient"
    x1="0%"
    y1="0%"
    x2="100%"
    y2="100%"
  >
    <stop
      offset="0%"
      stopColor="#bfeaf5"
    />

    <stop
      offset="45%"
      stopColor="#79c8df"
    />

    <stop
      offset="100%"
      stopColor="#4fa8c7"
    />
  </linearGradient>


  {/* =========================================================
      PATRÓN DE ONDAS
      ========================================================= */}

  <pattern
    id="publicWaterPattern"
    patternUnits="userSpaceOnUse"
    width="90"
    height="45"
    patternTransform="rotate(-8)"
  >

    <path
      d="
        M -20 12
        C 0 4, 20 4, 40 12
        S 80 20, 110 12
      "
      fill="none"
      stroke="#ffffff"
      strokeWidth="2"
      strokeOpacity="0.42"
    />

    <path
      d="
        M -30 28
        C -10 20, 10 20, 30 28
        S 70 36, 100 28
      "
      fill="none"
      stroke="#ffffff"
      strokeWidth="1.6"
      strokeOpacity="0.30"
    />

    <path
      d="
        M -10 42
        C 10 34, 30 34, 50 42
        S 90 50, 120 42
      "
      fill="none"
      stroke="#ffffff"
      strokeWidth="1.2"
      strokeOpacity="0.22"
    />

  </pattern>

</defs>
        {/* =====================================================
    CAPAS BASE DEL PLANO
    Calles, aceras, lagunas, áreas verdes,
    equipamientos, límites, etc.
    ===================================================== */}

{visibleOverlays
  .filter((layer) => layer.category !== "label")
  .map((layer) => (
    <PublicOverlay
      key={layer.id}
      availableParcels={availableParcels}
      layer={layer}
    />
  ))}

{/* =====================================================
    LOTES DISPONIBLES
    Se dibujan DESPUÉS de las capas base para que
    el color de disponibilidad quede visible.
    ===================================================== */}

{plan.lots.map((parcel) => {
  const available = lotsByPlan.has(parcel.id);

  return (
    <polygon
      key={parcel.id}
      className={
        available
          ? "available"
          : "unavailable"
      }
      data-testid={
        available
          ? `public-plan-lot-${parcel.id}`
          : undefined
      }
      points={parcel.points
        .map((point) => point.join(","))
        .join(" ")}
      style={{
        pointerEvents: available
          ? "auto"
          : "none",
        cursor: available
          ? "pointer"
          : "default",
      }}
      onPointerDown={(event) => {
  if (!available) return;

  event.preventDefault();

  /*
   * En touch NO detenemos la propagación,
   * porque el SVG necesita recibir el segundo
   * dedo para activar el pinch zoom.
   */
  if (event.pointerType !== "touch") {
    event.stopPropagation();
  }

  suppressClickRef.current = false;
}}
      onClick={(event) => {
  handleLotClick(event, parcel);
}}
    />
  );
})}

{/* =====================================================
    ETIQUETAS
    Números de lote y números de manzano van AL FINAL
    para que nunca queden ocultos.
    ===================================================== */}

{visibleOverlays
  .filter((layer) => layer.category === "label")
  .map((layer) => (
    <PublicOverlay
      key={layer.id}
      availableParcels={availableParcels}
      layer={layer}
    />
  ))}
      </svg>

      {/* =======================================================
          DETALLE DEL LOTE
          ======================================================= */}

      {selectedLot && (
        <div className="public-plan-detail">
          <button
            aria-label="Cerrar información"
            className="icon-button"
            data-testid="close-public-plan-detail"
            onClick={() =>
              setSelected(null)
            }
            type="button"
          >
            <X size={18} />
          </button>

          <div className="public-plan-detail-status">
            DISPONIBLE
          </div>

          <h2>
            Manzano {selectedLot.manzano} · Lote{" "}
            {selectedLot.lote}
          </h2>

          {visibility.show_surface && (
            <div>
              <strong>
                Superficie
              </strong>

              <span>
                {selectedLot.superficie} m²
              </span>
            </div>
          )}

          {visibility.show_price_sqm && (
            <div>
              <strong>
                Precio por m²
              </strong>

              <span>
                US$ {selectedLot.precio_m2}
              </span>
            </div>
          )}

          {visibility.show_total_price && (
            <div>
              <strong>
                Precio total
              </strong>

              <span>
                US$ {selectedLot.precio_total}
              </span>
            </div>
          )}

          {visibility.show_phase && (
            <div>
              <strong>
                Fase
              </strong>

              <span>
                {selectedLot.fase || "—"}
              </span>
            </div>
          )}

          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="public-plan-whatsapp-button"
            >
              <MessageCircle size={18} />
              Cotizar este terreno
            </a>
          )}
        </div>
      )}
    </div>
  );
}